import { StreamingSectionKey } from 'fym-shared/src/music24/streaming';

/**
 * The addresses of the Streaming sections (design your-intelligence h4). The app router adds the
 * `/app` base. The period travels as `?days=` from section to section.
 */
export type StreamingSection =
  | 'overview'
  | 'tracks'
  | 'track'
  | 'stores'
  | 'countries'
  | 'audience'
  | 'playSources'
  | 'shortVideo'
  | 'playlists'
  | 'accelerator'
  | 'earnings';

/**
 * The sections that the chips under the title open, in their order (design streaming-sections e1:
 * your music, your listeners, your money).
 */
export const STREAMING_CHIP_SECTIONS = [
  'overview',
  'tracks',
  'stores',
  'countries',
  'audience',
  'playSources',
  'shortVideo',
  'playlists',
  'accelerator',
  'earnings',
] as const;
export type StreamingChipSection = (typeof STREAMING_CHIP_SECTIONS)[number];

/**
 * The chips whose data comes from a source that reports it (Stores, Countries, Earnings). A chip is
 * dashed only when no connected source gives that data; the section is then empty, not locked.
 */
export const SOURCE_SECTION_CHIPS: Partial<Record<StreamingChipSection, StreamingSectionKey>> = {
  stores: 'stores',
  countries: 'countries',
  audience: 'audience',
  playSources: 'playSources',
  shortVideo: 'shortVideo',
  accelerator: 'accelerator',
  earnings: 'earnings',
};

/**
 * The chips that are not drawn when no connected source gives their data (design streaming-sections
 * e1): only TuneCore gives Accelerator, so a dashed chip would only advertise TuneCore.
 */
export const HIDDEN_WHEN_EMPTY_CHIPS: readonly StreamingChipSection[] = ['accelerator'];

/** The Catalog screen, where every source is connected, reconnected and disconnected. */
export const CATALOG_PATH = '/catalog';

const SECTION_PATHS: Record<Exclude<StreamingSection, 'track'>, string> = {
  overview: '/streaming',
  tracks: '/streaming/tracks',
  stores: '/streaming/stores',
  countries: '/streaming/countries',
  audience: '/streaming/audience',
  playSources: '/streaming/playSources',
  shortVideo: '/streaming/shortVideo',
  accelerator: '/streaming/accelerator',
  playlists: '/streaming/playlists',
  earnings: '/streaming/earnings',
};

function withQuery(path: string, query: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  });
  const text = params.toString();
  return text === '' ? path : `${path}?${text}`;
}

/** The address of a section, with the period. */
export function streamingSectionPath(section: Exclude<StreamingSection, 'track'>, days?: number): string {
  return withQuery(SECTION_PATHS[section], { days });
}

/** The address of one track. The key is a `StreamingTrackRow.key`. */
export function streamingTrackPath(trackKey: string, days?: number): string {
  return withQuery(`/streaming/tracks/${encodeURIComponent(trackKey)}`, { days });
}

/** The address of the detail of one store or country. */
export function streamingDimensionPath(dimension: 'store' | 'country', value: string, days?: number): string {
  const section = dimension === 'store' ? 'stores' : 'countries';
  return withQuery(SECTION_PATHS[section], { days, [dimension]: value });
}
